import { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import SkeletonBone, { SkeletonStack } from '../../../../components/SkeletonBone';
import { styles } from '../../../../styles/themes/style';
import { useAppActions } from '../../../../stores/application/useAppActions';
import { useSignalsRequest } from '../../../../services/apis/signals/useSignalsRequest';
import LeadsPagination from '../campaigns/LeadsPagination';
import AddToCampaignDialog from './AddToCampaignDialog';
import {
  ISignal, ISignalLead, ISignalLeadsResponse, ISignalSettings, SIGNAL_STATUS_LABELS, SIGNAL_TYPE_LABELS,
  SignalLeadSort, SignalLeadStatus, SignalLeadStatusFilter, SignalType,
} from '../../../../interfaces/models/signals';
import {
  Avatar, Card, CardHead, CardTitle, GhostButton, Icon, Muted, Pill, PrimaryButton, SIGNAL_TYPE_ICONS,
  STATUS_TONES, Select, TextInput, fromNow, personName, personUrl, plural,
} from './signalsUi';

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const TABS: { id: SignalLeadStatusFilter; label: string }[] = [
  { id: 'open', label: 'To review' },
  { id: 'approved', label: 'Approved' },
  { id: 'in_campaign', label: 'In campaign' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'low_fit', label: 'Low fit' },
  { id: 'all', label: 'All' },
];

function tabCount(tab: SignalLeadStatusFilter, counts: ISignalLeadsResponse['counts'] | null): number | null {
  if (!counts) return null;
  // 'new' already leaves out low fits; they have their own tab.
  if (tab === 'open') return counts.new + counts.approved;
  if (tab === 'all') return counts.new + counts.approved + counts.dismissed + counts.in_campaign + (counts.low_fit ?? 0);
  return counts[tab] ?? 0;
}

/** The fit score as a coloured badge, against the workspace's two scores. */
const FitBadge: FunctionComponent<{ lead: ISignalLead; settings: ISignalSettings | null }> = ({ lead, settings }) => {
  if (lead.fitScore === null || lead.fitScore === undefined) {
    return <Muted>{settings?.hasIcp ? 'Scoring…' : '—'}</Muted>;
  }
  const high = lead.fitScore >= (settings?.autoApproveScore ?? 80);
  const low = lead.fitScore < (settings?.ignoreScore ?? 40);
  const tone = high
    ? { bg: styles.colors.green200, fg: '#0f9d6c' }
    : low ? { bg: styles.colors.black100, fg: styles.colors.black400 } : { bg: styles.colors.yellow200, fg: '#c48a00' };
  const stale = settings && lead.fitIcpVersion !== null && lead.fitIcpVersion !== settings.icpVersion;
  return (
    <TooltipWrapper tooltipText={`${lead.fitReason || 'Fit against your ideal buyer.'}${stale ? ' (re-scoring for the new ideal buyer)' : ''}`}>
      <Pill $bg={tone.bg} $fg={tone.fg}>{lead.fitScore}{stale ? '*' : ''}</Pill>
    </TooltipWrapper>
  );
};

/** One signal in the expanded row, in a sentence. */
function describeSignal(signal: ISignal): string {
  const c = signal.context || {};
  switch (signal.type) {
    case 'profile_view': return c.lastVisited ? `Viewed the profile ${c.lastVisited}` : 'Viewed the profile';
    case 'post_comment': return c.commentText ? `Commented: “${c.commentText}”` : 'Commented on a post';
    case 'post_reaction': return `Reacted${c.reaction ? ` (${c.reaction.toLowerCase()})` : ''} to a post`;
    case 'job_change': return c.newTitle ? `Started as ${c.newTitle}${c.newCompany ? ` at ${c.newCompany}` : ''}` : 'Changed jobs';
    default: return 'Signal';
  }
}

interface ISignalLeadsPanelProps {
  /** Bumped by the page when sources or settings change, so the list refreshes too. */
  refreshKey: number;
  /** For the fit badge colours; null until loaded. */
  settings: ISignalSettings | null;
}

/**
 * The people the radar found, newest signal first. A person is one row however many
 * signals they gave, with one owner (the first account that saw them). Review here:
 * approve, dismiss, or add straight into one of your campaigns.
 */
const SignalLeadsPanel: FunctionComponent<ISignalLeadsPanelProps> = ({ refreshKey, settings }) => {
  const api = useSignalsRequest();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { setToast } = useAppActions();

  const [tab, setTab] = useState<SignalLeadStatusFilter>('open');
  const [type, setType] = useState<SignalType | ''>('');
  const [owner, setOwner] = useState<'me' | ''>('');
  const [sort, setSort] = useState<SignalLeadSort>('recent');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<ISignalLeadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, ISignal[] | null>>({});
  const [campaignLeads, setCampaignLeads] = useState<ISignalLead[] | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    apiRef.current.getLeads({ status: tab, type, owner, search, sort, page, limit: pageSize }).then(res => {
      if (stale) return;
      setLoading(false);
      if (res?.success) {
        setData(res.data);
        setSelected(new Set());
      } else {
        setToast({ type: 'error', message: res?.message || 'Could not load Signals.' });
      }
    });
    return () => { stale = true; };
    // setToast is stable for the lifetime of the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, type, owner, search, sort, page, pageSize, reloadKey, refreshKey]);

  const reload = () => setReloadKey(k => k + 1);
  const leads = data?.leads ?? [];
  const selectedLeads = leads.filter(l => selected.has(l._id));
  const allChecked = leads.length > 0 && leads.every(l => selected.has(l._id));

  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(leads.map(l => l._id)));
  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setStatus = async (ids: string[], status: SignalLeadStatus) => {
    if (!ids.length) return;
    setBusy(true);
    const res = await apiRef.current.updateLeadStatus(ids, status);
    setBusy(false);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not update these people.' });
      return;
    }
    const verb = status === 'approved' ? 'approved' : status === 'dismissed' ? 'dismissed' : 'moved back to review';
    setToast({ type: 'success', message: `${plural(ids.length, 'person', 'people')} ${verb}.` });
    reload();
  };

  const toggleExpand = async (lead: ISignalLead) => {
    if (expanded === lead._id) { setExpanded(null); return; }
    setExpanded(lead._id);
    if (details[lead._id] !== undefined) return;
    setDetails(prev => ({ ...prev, [lead._id]: null }));
    const res = await apiRef.current.getLeadSignals(lead._id);
    setDetails(prev => ({ ...prev, [lead._id]: res?.success ? (res.data?.signals ?? []) : [] }));
  };

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>People showing interest</CardTitle>
          <Muted>One row per person, owned by the teammate whose account saw them first.</Muted>
        </div>
      </CardHead>

      <Tabs role="tablist">
        {TABS.map(t => {
          const n = tabCount(t.id, data?.counts ?? null);
          return (
            <TabBtn
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              $active={tab === t.id}
              onClick={() => { setTab(t.id); setPage(1); }}
            >
              {t.label}{n !== null && <TabCount $active={tab === t.id}>{n.toLocaleString()}</TabCount>}
            </TabBtn>
          );
        })}
      </Tabs>

      <Toolbar>
        <SearchBox>
          <Icon name="search" size={15} />
          <TextInput
            type="search"
            placeholder="Search name or headline"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            name="signals-search"
            autoComplete="off"
          />
        </SearchBox>
        <Select value={type} onChange={e => { setType(e.target.value as SignalType | ''); setPage(1); }} aria-label="Signal type">
          <option value="">All signals</option>
          {(Object.keys(SIGNAL_TYPE_LABELS) as SignalType[]).map(t => (
            <option key={t} value={t}>{SIGNAL_TYPE_LABELS[t]}s</option>
          ))}
        </Select>
        <Select value={owner} onChange={e => { setOwner(e.target.value as 'me' | ''); setPage(1); }} aria-label="Owner">
          <option value="">Everyone’s</option>
          <option value="me">Mine</option>
        </Select>
        <Select value={sort} onChange={e => { setSort(e.target.value as SignalLeadSort); setPage(1); }} aria-label="Sort">
          <option value="recent">Newest signal first</option>
          <option value="fit">Best fit first</option>
        </Select>
      </Toolbar>

      {selectedLeads.length > 0 && (
        <BulkBar>
          <strong>{plural(selectedLeads.length, 'person', 'people')} selected</strong>
          <PrimaryButton type="button" disabled={busy} onClick={() => setCampaignLeads(selectedLeads)}>
            <Icon name="send" size={14} /> Add to campaign
          </PrimaryButton>
          {tab !== 'approved' && (
            <GhostButton type="button" disabled={busy} onClick={() => setStatus(selectedLeads.map(l => l._id), 'approved')}>
              <Icon name="check" size={14} /> Approve
            </GhostButton>
          )}
          {tab !== 'dismissed' && (
            <GhostButton type="button" $danger disabled={busy} onClick={() => setStatus(selectedLeads.map(l => l._id), 'dismissed')}>
              <Icon name="x" size={14} /> Dismiss
            </GhostButton>
          )}
          {tab === 'dismissed' && (
            <GhostButton type="button" disabled={busy} onClick={() => setStatus(selectedLeads.map(l => l._id), 'new')}>
              <Icon name="undo" size={14} /> Restore
            </GhostButton>
          )}
        </BulkBar>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all on this page" />
              </th>
              <th>Person</th>
              <th>Fit</th>
              <th>Latest signal</th>
              <th>When</th>
              <th>Owner</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && !data && [0, 1, 2, 3].map(i => (
              <tr key={`sk-${i}`}>
                <td colSpan={8}>
                  <SkeletonStack>
                    <SkeletonBone $w={['46%', '34%', '52%', '40%'][i]} $h="14px" />
                    <SkeletonBone $w="26%" $h="11px" />
                  </SkeletonStack>
                </td>
              </tr>
            ))}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <EmptyRow>
                    <Icon name="radar" size={28} />
                    {tab === 'open'
                      ? 'Nobody new yet. Turn on a source above — the first results usually arrive within a day.'
                      : 'Nobody here.'}
                  </EmptyRow>
                </td>
              </tr>
            )}
            {leads.map(lead => {
              const name = personName(lead.person);
              const url = personUrl(lead.person);
              const tone = STATUS_TONES[lead.status];
              const isOpen = expanded === lead._id;
              const signals = details[lead._id];
              return (
                <Fragment key={lead._id}>
                  <Row $selected={selected.has(lead._id)}>
                    <td>
                      <input type="checkbox" checked={selected.has(lead._id)} onChange={() => toggleOne(lead._id)} aria-label={`Select ${name}`} />
                    </td>
                    <td>
                      <PersonCell>
                        <Avatar person={lead.person} />
                        <PersonText>
                          <PersonName>
                            {url ? <a href={url} target="_blank" rel="noreferrer">{name}</a> : name}
                            {lead.isConnection && <Pill $bg={styles.colors.green200} $fg="#0f9d6c">Connected</Pill>}
                          </PersonName>
                          {lead.person.headline && <Headline title={lead.person.headline}>{lead.person.headline}</Headline>}
                        </PersonText>
                      </PersonCell>
                    </td>
                    <td><FitBadge lead={lead} settings={settings} /></td>
                    <td>
                      <SignalCell type="button" onClick={() => toggleExpand(lead)} aria-expanded={isOpen}>
                        <Summary>{lead.lastSignal?.summary || '—'}</Summary>
                        <Types>
                          {lead.signalTypes.map(t => (
                            <TooltipWrapper key={t} tooltipText={SIGNAL_TYPE_LABELS[t]}>
                              <TypeIcon><Icon name={SIGNAL_TYPE_ICONS[t]} size={13} /></TypeIcon>
                            </TooltipWrapper>
                          ))}
                          {lead.signalCount > 1 && <MoreCount>{plural(lead.signalCount, 'signal')}</MoreCount>}
                          <Chevron $open={isOpen}><Icon name="chevron" size={13} /></Chevron>
                        </Types>
                      </SignalCell>
                    </td>
                    <td><Muted>{fromNow(lead.lastSignalAt)}</Muted></td>
                    <td><Muted>{lead.owner ? [lead.owner.firstName, lead.owner.lastName].filter(Boolean).join(' ') : '—'}</Muted></td>
                    <td>
                      <Pill $bg={tone.bg} $fg={tone.fg}>{SIGNAL_STATUS_LABELS[lead.status]}</Pill>
                      {lead.autoEnrolledAt && <AutoNote>by autopilot</AutoNote>}
                      {lead.status === 'in_campaign' && lead.campaign && (
                        <CampaignLink to={`/campaigns/${lead.campaign._id}/leads`}>{lead.campaign.name}</CampaignLink>
                      )}
                    </td>
                    <td>
                      <Actions>
                        {lead.status !== 'in_campaign' && (
                          <TooltipWrapper tooltipText="Add to campaign">
                            <IconAction type="button" onClick={() => setCampaignLeads([lead])} aria-label="Add to campaign">
                              <Icon name="send" size={15} />
                            </IconAction>
                          </TooltipWrapper>
                        )}
                        {lead.status === 'new' && (
                          <TooltipWrapper tooltipText="Approve">
                            <IconAction type="button" disabled={busy} onClick={() => setStatus([lead._id], 'approved')} aria-label="Approve">
                              <Icon name="check" size={15} />
                            </IconAction>
                          </TooltipWrapper>
                        )}
                        {(lead.status === 'new' || lead.status === 'approved') && (
                          <TooltipWrapper tooltipText="Dismiss">
                            <IconAction type="button" $danger disabled={busy} onClick={() => setStatus([lead._id], 'dismissed')} aria-label="Dismiss">
                              <Icon name="x" size={15} />
                            </IconAction>
                          </TooltipWrapper>
                        )}
                        {lead.status === 'dismissed' && (
                          <TooltipWrapper tooltipText="Restore">
                            <IconAction type="button" disabled={busy} onClick={() => setStatus([lead._id], 'new')} aria-label="Restore">
                              <Icon name="undo" size={15} />
                            </IconAction>
                          </TooltipWrapper>
                        )}
                      </Actions>
                    </td>
                  </Row>
                  {isOpen && (
                    <DetailRow>
                      <td />
                      <td colSpan={7}>
                        {signals === null || signals === undefined
                          ? <Muted>Loading signals…</Muted>
                          : (
                            <SignalList>
                              {signals.map(s => (
                                <SignalItem key={s._id}>
                                  <TypeIcon><Icon name={SIGNAL_TYPE_ICONS[s.type]} size={13} /></TypeIcon>
                                  <span>{describeSignal(s)}</span>
                                  {s.context?.postUrl && (
                                    <PostLink href={s.context.postUrl} target="_blank" rel="noreferrer" title={s.context.postExcerpt || 'Open post'}>
                                      post <Icon name="external" size={11} />
                                    </PostLink>
                                  )}
                                  <Muted>· {fromNow(s.occurredAt)}</Muted>
                                </SignalItem>
                              ))}
                              {!signals.length && <Muted>No signals kept for this person.</Muted>}
                            </SignalList>
                          )}
                      </td>
                    </DetailRow>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {!!data && data.total > 0 && (
        <LeadsPagination
          page={page}
          pageSize={pageSize}
          total={data.total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={size => { setPageSize(size); setPage(1); }}
        />
      )}

      <AddToCampaignDialog
        show={!!campaignLeads}
        leads={campaignLeads ?? []}
        onHide={() => setCampaignLeads(null)}
        onDone={reload}
      />
    </Card>
  );
};

export default SignalLeadsPanel;

// ─── Styles ───────────────────────────────────────────────────────────────────

const Tabs = styled.div`
  display: flex; gap: 20px; flex-wrap: wrap;
  border-bottom: 1px solid #eef1f5;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0 0 10px; margin-bottom: -1px;
  border: none; background: none; cursor: pointer;
  border-bottom: 2px solid ${({ $active }) => ($active ? styles.colors.primary600 : 'transparent')};
  font-family: NunitoSans; font-size: 14px; font-weight: 700;
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};
`;

const TabCount = styled.span<{ $active: boolean }>`
  padding: 0 7px; border-radius: 9999px; font-size: 12px; line-height: 18px;
  background: ${({ $active }) => ($active ? styles.colors.primary100 : styles.colors.black100)};
`;

const Toolbar = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`;

const SearchBox = styled.label`
  position: relative; flex: 1; min-width: 200px; max-width: 360px;
  display: flex; align-items: center;
  > svg { position: absolute; left: 11px; color: ${styles.colors.black300}; pointer-events: none; }
  > input { width: 100%; padding-left: 32px; }
`;

const BulkBar = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 10px 14px; border-radius: 12px; background: ${styles.colors.primary100};
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  strong { margin-right: auto; }
`;

const TableWrap = styled.div`
  width: 100%; overflow-x: auto;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; min-width: 860px;
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  th {
    text-align: left; padding: 8px 10px;
    font-size: 12px; font-weight: 700; color: ${styles.colors.black400};
    border-bottom: 1px solid #eef1f5; white-space: nowrap;
  }
  td { padding: 10px; border-bottom: 1px solid #f2f4f7; vertical-align: middle; }
  input[type='checkbox'] { accent-color: ${styles.colors.primary600}; cursor: pointer; }
`;

const Row = styled.tr<{ $selected: boolean }>`
  background: ${({ $selected }) => ($selected ? '#f7f8ff' : 'transparent')};
  &:hover { background: #f9fafc; }
`;

const DetailRow = styled.tr`
  background: #fbfcfe;
  td { padding-top: 4px; padding-bottom: 14px; }
`;

const PersonCell = styled.div`
  display: flex; align-items: center; gap: 10px; min-width: 220px;
`;

const PersonText = styled.div`
  display: flex; flex-direction: column; min-width: 0;
`;

const PersonName = styled.span`
  display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-weight: 700;
  a { color: ${styles.colors.black600}; text-decoration: none; }
  a:hover { color: ${styles.colors.primary600}; }
`;

const Headline = styled.span`
  font-size: 12px; color: ${styles.colors.black400};
  max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const SignalCell = styled.button`
  display: flex; flex-direction: column; gap: 4px; align-items: flex-start;
  border: none; background: none; padding: 0; cursor: pointer; text-align: left;
  font-family: NunitoSans; color: ${styles.colors.black600};
  max-width: 360px;
`;

const Summary = styled.span`
  font-size: 13px; line-height: 18px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;

const Types = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
`;

const TypeIcon = styled.span`
  width: 22px; height: 22px; border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: ${styles.colors.primary100}; color: ${styles.colors.primary600};
`;

const MoreCount = styled.span`
  font-size: 12px; color: ${styles.colors.black400}; margin-left: 2px;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex; color: ${styles.colors.black300};
  transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg); transition: transform 0.15s ease;
`;

const CampaignLink = styled(Link)`
  display: block; margin-top: 4px; max-width: 160px;
  font-size: 12px; color: ${styles.colors.primary600}; text-decoration: none;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  &:hover { text-decoration: underline; }
`;

const AutoNote = styled.span`
  display: block; margin-top: 3px;
  font-size: 11px; color: ${styles.colors.black300};
`;

const Actions = styled.div`
  display: flex; gap: 4px; justify-content: flex-end;
`;

const IconAction = styled.button<{ $danger?: boolean }>`
  width: 30px; height: 30px; border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid #e3e7ee; background: ${styles.colors.white}; cursor: pointer;
  color: ${({ $danger }) => ($danger ? styles.colors.red600 : styles.colors.black500)};
  &:hover:not(:disabled) { background: ${({ $danger }) => ($danger ? styles.colors.red200 : styles.colors.primary100)}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const EmptyRow = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 36px 12px; color: ${styles.colors.black400}; text-align: center;
  svg { opacity: 0.4; }
`;

const SignalList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;

const SignalItem = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 13px;
`;

const PostLink = styled.a`
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 12px; color: ${styles.colors.primary600}; text-decoration: none;
  &:hover { text-decoration: underline; }
`;
